// "Edit aircraft details" modal of a charter booking: operator, then that operator's aircraft.
import type { UseQueryResult } from '@tanstack/react-query';
import { Input, Modal, Select, Space, Spin, Typography } from 'antd';

import { listAircraft, listOperators } from '../../api/endpoints';
import type { CharterBookingDetail } from '../../api/types';
import { formatCategory } from '../../data/airportLabel';
import type { CharterBookingUpdate } from './charterBookingUpdate';

const { Text } = Typography;

export function AircraftModal({
  b,
  aircraftOpen,
  setAircraftOpen,
  submitAircraft,
  submitGuard,
  saveMut,
  operatorId,
  setOperatorId,
  aircraftId,
  setAircraftId,
  operatorsQ,
  aircraftQ,
  chosenAircraft,
}: {
  b: CharterBookingDetail;
  aircraftOpen: boolean;
  setAircraftOpen: (open: boolean) => void;
  submitAircraft: () => void;
  submitGuard: { busy: boolean };
  saveMut: { isPending: boolean; mutate: (body: CharterBookingUpdate) => void };
  operatorId: string | undefined;
  setOperatorId: (id: string | undefined) => void;
  aircraftId: string | undefined;
  setAircraftId: (id: string | undefined) => void;
  operatorsQ: UseQueryResult<Awaited<ReturnType<typeof listOperators>>>;
  aircraftQ: UseQueryResult<Awaited<ReturnType<typeof listAircraft>>>;
  chosenAircraft: Awaited<ReturnType<typeof listAircraft>>['items'][number] | undefined;
}) {
  return (
    <Modal
      title="Edit aircraft details"
      open={aircraftOpen}
      onCancel={() => setAircraftOpen(false)}
      onOk={submitAircraft}
      confirmLoading={submitGuard.busy || saveMut.isPending}
      okText="Save"
      destroyOnClose
    >
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        This booking currently shows{' '}
        <Text strong>
          {[b.operatorName, b.modelName, b.tailNumber].filter(Boolean).join(' · ') || '—'}
        </Text>
        . Choosing another aircraft records a correction on the booking; the quote the customer
        accepted is not changed.
      </Text>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Operator</Text>
          <Select
            value={operatorId}
            onChange={(v) => {
              setOperatorId(v);
              setAircraftId(undefined);
            }}
            loading={operatorsQ.isLoading}
            placeholder="Select operator"
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            options={(operatorsQ.data?.items ?? []).map((o) => ({ value: o.id, label: o.name }))}
          />
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Aircraft model</Text>
          <Select
            value={aircraftId}
            onChange={setAircraftId}
            loading={aircraftQ.isLoading}
            disabled={!operatorId}
            placeholder={operatorId ? 'Select aircraft' : 'Select an operator first'}
            style={{ width: '100%' }}
            showSearch
            optionFilterProp="label"
            notFoundContent={aircraftQ.isLoading ? <Spin size="small" /> : 'No aircraft for this operator'}
            options={(aircraftQ.data?.items ?? []).map((a) => ({
              value: a.id,
              label: [a.modelName, a.tailNumber].filter(Boolean).join(' · '),
            }))}
          />
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Category</Text>
          <Input
            readOnly
            value={chosenAircraft ? formatCategory(chosenAircraft.category) : ''}
            placeholder="Derived from the selected aircraft"
          />
        </div>
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Seats</Text>
          <Input
            readOnly
            value={chosenAircraft?.seatingCapacity != null ? String(chosenAircraft.seatingCapacity) : ''}
            placeholder="Derived from the selected aircraft"
          />
        </div>
      </Space>
    </Modal>
  );
}
